import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import Logomark from "@/components/Logomark";
import { IconScore, IconRadar, IconBoard, IconTerminal, IconArrowRise, IconRise, IconShield } from "@/components/landing/icons";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";

/**
 * The navigation for every public page.
 *
 * Replaces a per-page header whose items were "#features", "#how" and
 * "#trust" — anchors that resolve only on the landing page, so on /pricing or
 * /security three of the four menu items silently did nothing.
 *
 * Every destination here is a real route. The product menu leads with the free
 * scan because it is the only thing on the site usable without an account, and
 * includes Radar and the MCP server, which are the two things no competitor
 * has and which the old nav never mentioned.
 *
 * `overlay` renders it transparent for pages that open on the dark hero, so the
 * nav does not cut a hard white line across it.
 *
 * Mounted by PublicLayout, so it is the ONLY public navigation. PublicLayout
 * used to declare a second, different one.
 */
export const PRODUCT = [
  { id: "scan", to: "/ats-resume-checker", icon: IconScore, label: "Free ATS scan",
    note: "Score your resume — no account needed" },
  { id: "radar", to: "/features#radar", icon: IconRadar, label: "Job Radar",
    note: "Roles straight from company career pages" },
  { id: "tracker", to: "/features#tailor", icon: IconBoard, label: "Tailor & track",
    note: "Rewrite from your real history, then track it" },
  { id: "mcp", to: "/features#mcp", icon: IconTerminal, label: "MCP server",
    note: "Drive NextOpp from your own AI agent" },
  // The other two doors. Same index, different front page.
  { id: "early", to: "/early", icon: IconRise, label: "First jobs & students",
    note: "Internships, seasonal work, a first resume" },
  { id: "government", to: "/government", icon: IconShield, label: "Government jobs",
    note: "Federal, state and local, with closing dates" },
];

export default function PublicNav({ overlay = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Solid once the page has moved, so an overlay nav stays readable over
  // whatever scrolls under it.
  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlay]);

  // A drawer that survives a route change covers the page you just asked for.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
    };
    const onClick = (e) => {
      if (!menuRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const transparent = overlay && !scrolled;
  const link = transparent
    ? "text-white/75 hover:text-white"
    : "text-stone-700 hover:text-ink";

  return (
    <header
      data-testid="public-nav-root"
      // Dark, not transparent. A transparent bar only works if the section
      // behind it is genuinely underneath, which depends on negative margins
      // and stacking order lining up — and when that fails you get white text
      // on a white bar and an invisible menu, which is exactly what happened.
      // An opaque dark bar over a dark hero reads the same and cannot break.
      className={`sticky top-0 z-[60] transition-colors duration-300 ${
        transparent
          ? "bg-night-deep border-b border-white/10"
          : "bg-paper/85 backdrop-blur-xl border-b border-sand/70"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <Logomark />
          <span className={`font-heading font-bold tracking-tight text-lg ${transparent ? "text-white" : "text-ink"}`}>
            NextOpp
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm" data-testid="public-nav">
          <div className="relative">
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-haspopup="true"
              data-testid="nav-product-trigger"
              className={`inline-flex items-center gap-1 ${link}`}
            >
              Product
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>

            {open && (
              <div
                ref={menuRef}
                data-testid="nav-product-menu"
                className="absolute left-0 top-full mt-3 w-[23.5rem] rounded-sm bg-white border border-sand shadow-[0_28px_64px_-20px_rgba(5,7,15,0.55)] overflow-hidden"
              >
                {/* Lit along the top edge, like every other surface on this
                    site. A plain white rectangle dropped on the dark hero was
                    the one element that belonged to no system. */}
                <span
                  aria-hidden="true"
                  className="block h-px bg-gradient-to-r from-dawn-500/70 via-dawn-300/40 to-transparent"
                />
                <div className="p-2">
                  {PRODUCT.map((p) => (
                    <Link
                      key={p.id}
                      to={p.to}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-item-${p.id}`}
                      className="group flex items-start gap-3 p-2.5 rounded-sm hover:bg-paper transition-colors"
                    >
                      {/* The same night chip the workflow steps use, so a menu
                          item and a page section read as the same product. */}
                      <span className="w-8 h-8 shrink-0 grid place-items-center rounded-sm bg-paper-deep text-stone-700 transition-colors group-hover:bg-night group-hover:text-dawn-300">
                        <p.icon className="w-[17px] h-[17px]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-stone-900">
                          {p.label}
                          <IconArrowRise className="w-3.5 h-3.5 text-dawn-800 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                        </span>
                        <span className="block text-xs text-stone-600 leading-snug">{p.note}</span>
                      </span>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/features"
                  onClick={() => setOpen(false)}
                  data-testid="nav-item-all"
                  className="flex items-center justify-between gap-2 px-4 py-3 border-t border-sand bg-paper text-xs font-medium text-stone-700 hover:text-stone-950 transition-colors"
                >
                  See everything NextOpp does
                  <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
              </div>
            )}
          </div>

          <Link to="/guides" className={link} data-testid="nav-guides">Guides</Link>
          <Link to="/use-cases" className={link}>Use cases</Link>
          <Link to="/pricing" className={link} data-testid="nav-pricing">Pricing</Link>
          <Link to="/security" className={link}>Security</Link>
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          {/* Below md the nav above is `hidden`, which left public pages with
              no menu at all — every destination was reachable only from the
              footer. This is the same item set, in a drawer. */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-trigger"
            className={`md:hidden p-2 rounded-sm ${transparent ? "text-white/80 hover:text-white" : "text-stone-700 hover:text-stone-950"}`}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          {user ? (
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-night-lift hover:bg-night-lift text-white rounded-sm"
              data-testid="nav-go-app"
            >
              Open App <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <>
              <Link
                to="/login"
                className={`text-sm px-3 py-2 ${link}`}
                data-testid="nav-login-link"
              >
                Sign in
              </Link>
              <Button
                onClick={() => navigate("/signup")}
                className="bg-brand hover:bg-brand-hover text-white rounded-sm"
                data-testid="nav-signup-cta"
              >
                Start free
              </Button>
            </>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden border-t border-sand bg-white"
          data-testid="nav-mobile-menu"
        >
          <div className="px-4 py-3">
            <div className="u-label text-stone-600 px-2 pb-2">Product</div>
            {PRODUCT.map((p) => (
              <Link
                key={p.id}
                to={p.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-mobile-${p.id}`}
                className="flex items-start gap-3 p-2.5 rounded-sm hover:bg-paper"
              >
                <span className="w-8 h-8 shrink-0 grid place-items-center rounded-sm bg-paper-deep text-stone-700">
                  <p.icon className="w-[17px] h-[17px]" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-stone-900">{p.label}</span>
                  <span className="block text-xs text-stone-600 leading-snug">{p.note}</span>
                </span>
              </Link>
            ))}
            <div className="mt-2 pt-2 border-t border-sand flex flex-col">
              {[
                { to: "/compare", label: "Compare" },
                { to: "/guides", label: "Guides" },
                { to: "/use-cases", label: "Use cases" },
                { to: "/pricing", label: "Pricing" },
                { to: "/security", label: "Security" },
                { to: "/faq", label: "FAQ" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="px-2.5 py-2.5 text-sm font-medium text-stone-800 rounded-sm hover:bg-paper"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
